/**
 * @module components/NetworkLayer
 * @author Charles Blais <charles.blais@canada.ca>
 */

import React from 'react';
import PropTypes from 'prop-types';

import { LayerGroup, CircleMarker, Popup } from 'react-leaflet';

import StationPopupContent from './StationPopupContent';
import { Stations } from '../../libs/networks';
import { getStatusIndicator, getStatusColor } from '../../libs/display';

import { SET_SELECTED_STATION, useAppState } from '../../contexts/AppStateProvider';


const NetworksMapLayer = ({
  networkCode,
  stations,
  color,
  radius,
  showOpened,
  showClosed,
  showPlanned,
}) => {
  const [,dispatch] = useAppState();

  // Filter the list of stations but code only
  const targets = stations.map((station, i) => {

    // do we show it based on display properties
    let status = getStatusIndicator(station);
    let show = false;
    if( !show && showOpened && status === 0 ) show = true;
    if( !show && showClosed && status === -1 ) show = true;
    if( !show && showPlanned && status === 1 ) show = true;
    if( !show ) return null;

    // marker props are determined by the status
    const statusColor = getStatusColor(station);

    return (
      <CircleMarker
        key={i}
        center={[station.latitude, station.longitude]}
        color={color}
        fillColor={statusColor}
        radius={radius}
        fillOpacity={1}
        pathOptions={{ weight: 3 }}
      >
        <Popup
          onOpen={() => {
            dispatch({
              type: SET_SELECTED_STATION,
              payload: {
                networkCode: networkCode,
                station: station
              }
            })
          }}
        >
          <StationPopupContent
            networkCode={networkCode}
            station={station}
          />
        </Popup>
      </CircleMarker>
    )
  })

  return (
    <LayerGroup>
      {targets}
    </LayerGroup>
  );
};


NetworksMapLayer.propTypes = {
  /**
   * Network code of the selected network
   */
  networkCode: PropTypes.string.isRequired,
  /**
   * List of stations
   */
  stations: PropTypes.instanceOf(Stations),
  /**
   * Color to be used for markers
   */
  color: PropTypes.string,
  /**
   * Radius of the markers
   */
  radius: PropTypes.number,
  /**
   * Are we displaying closed stations?
   */
  showOpened: PropTypes.bool,
  /**
   * Are we displaying closed stations?
   */
  showClosed: PropTypes.bool,
  /**
   * Are we displaying closed stations?
   */
  showPlanned: PropTypes.bool,
};


NetworksMapLayer.defaultProps = {
  stations: new Stations(),
  color: '#CD6155',
  radius: 6,
  showClosed: true,
  showOpened: true,
  showPlanned: true,
};

export default NetworksMapLayer;
